import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import styles from './Image.module.css';
import { borderVariants, type BorderVariants } from '../../utils/border/border';
import {
  backgroundColorVariants,
  type BackgroundVariants,
} from '../../utils/backgroundColor/backgroundColor';
import { altoConsole } from '../../utils/console/console';
import {
  WithResponsiveProps,
  getBaseValueOrUndefined,
  generateResponsiveClasses,
} from '../../utils/breakpoint/responsiveSSR';
import { responsiveToCssVars } from '../../utils/breakpoint/responsiveToCssVars';

const imageVariants = cva(styles.image, {
  variants: {
    fit: {
      contain: styles['fit--contain'],
      cover: styles['fit--cover'],
      fill: styles['fit--fill'],
      none: styles['fit--none'],
      scaleDown: styles['fit--scale-down'],
      'scale-down': styles['fit--scale-down'],
    },
  },
});

export interface ImageBaseProps
  extends Omit<
      React.ImgHTMLAttributes<HTMLImageElement>,
      'className' | 'style'
    >,
    Pick<BorderVariants, 'borderRadius'>,
    BackgroundVariants,
    VariantProps<typeof imageVariants> {
  /**
   * Image url that will be used as a fallback in case `src` prop is not set or image cannot be loaded
   */
  fallbackSrc?: string;
  /**
   * Called when image fails to load
   */
  onError?: (event: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  /**
   * Called when image is loaded
   */
  onLoad?: React.ReactEventHandler<HTMLImageElement>;
  /**
   * Polymorphism with children, the component will render the children instead of the image
   */
  asChild?: boolean;
  /**
   * To support polymorphism with children
   */
  children?: React.ReactNode;
  /**
   * **[Responsive]** Aspect ratio of the image
   */
  aspectRatio?: number;
}

type ResponsiveKeys = 'fit' | 'aspectRatio';

export type ImageProps = WithResponsiveProps<ImageBaseProps, ResponsiveKeys>;

const useImageLoaded = (
  onLoadProp?: React.ReactEventHandler<HTMLImageElement>,
) => {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement | null>(null);

  const handleLoad = React.useCallback(
    (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
      if (typeof onLoadProp === 'function') {
        onLoadProp(event);
      }
      setLoaded(true);
    },
    [onLoadProp],
  );

  useEffect(() => {
    if (ref.current && ref.current.complete) {
      setLoaded(true);
    }
  }, []);

  return { ref, loaded, handleLoad };
};

// to handle internal and forwarded refs
const useMergedRef = <T,>(
  internalRef: React.MutableRefObject<T | null>,
  forwardedRef: React.ForwardedRef<T>,
) => {
  return useMemo<React.RefCallback<T>>(
    () => (node: T | null) => {
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef && 'current' in forwardedRef) {
        (forwardedRef as React.MutableRefObject<T | null>).current = node;
      }
      internalRef.current = node;
    },
    [internalRef, forwardedRef],
  );
};

export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      src,
      fallbackSrc,
      onError: onErrorProp,
      onLoad: onLoadProp,
      asChild,
      children,
      borderRadius,
      backgroundColor,
      fit,
      aspectRatio,
      ...props
    },
    forwardedRef,
  ) => {
    const { ref: internalRef, loaded, handleLoad } = useImageLoaded(onLoadProp);
    const ref = useMergedRef(internalRef, forwardedRef);

    const [isError, setError] = useState(!src);

    const getResponsiveClasses = generateResponsiveClasses(styles);

    const baseFit = getBaseValueOrUndefined(fit);
    const fitResponsive = getResponsiveClasses(fit, 'fit');

    const aspectRatioVars = responsiveToCssVars(aspectRatio, {
      varPrefix: '--image-aspect-ratio',
      normalise: String,
    });

    if (asChild && !children) {
      altoConsole.error('Children required when asChild is `true`', {
        scope: 'Image',
      });
    }

    const handleError = React.useCallback(
      (e: React.SyntheticEvent<HTMLImageElement>) => {
        if (fallbackSrc) {
          setError(true);
        }

        if (typeof onErrorProp === 'function') {
          onErrorProp(e);
        }
      },
      [fallbackSrc, onErrorProp],
    );

    const imgProps = {
      src: isError ? fallbackSrc : src,
      className: [
        borderVariants({ borderRadius }),
        backgroundColorVariants({ backgroundColor }),
        imageVariants({ fit: baseFit }),
        ...fitResponsive,
      ]
        .filter(Boolean)
        .join(' '),
      onError: handleError,
      onLoad: handleLoad,
      ref,
      'data-loaded': loaded,
      style: {
        ...aspectRatioVars,
      },
      ...props,
    };

    if (asChild && React.isValidElement(children)) {
      const childProps =
        typeof children.props === 'object' && children.props !== null
          ? children.props
          : {};
      return React.cloneElement(children, {
        ...imgProps,
        ...childProps,
      });
    }

    return <img {...imgProps} />;
  },
);

Image.displayName = 'Image';
export default Image;
